/**
 * Google Classroom-style course viewer.
 * Layout: left sidebar (module/lesson nav) + right lesson player.
 * Progress bar and completion tracking per lesson.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  ChevronRight,
  CheckCircle,
  Circle,
  BookOpen,
  Video,
  FileText,
  Play,
  Lock,
} from 'lucide-react';
import { getMemberToken, getMemberData, isAuthenticated } from '@/lib/memberAuth';
import { SurveyFill } from '@/components/surveys/SurveyFill';
import { slidesEmbedSrc } from '@/src/app/workforce/courses/slidesEmbed';
import { getWeaveinApiUrl } from '@/src/config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Lesson {
  id: number;
  title: string;
  content_type: 'text' | 'video' | 'form_embed' | 'slides' | 'quiz' | 'survey' | 'platform_survey';
  content: Record<string, unknown>;
  sort_order: number;
  completed: boolean;
  translations?: Record<string, { title?: string; content?: Record<string, unknown> }>;
}

interface Module {
  id: number;
  title: string;
  description: string | null;
  sort_order: number;
  lessons: Lesson[];
}

interface CourseDetail {
  id: number;
  title: string;
  description: string | null;
  enrollment_id: number | null;
  enrollment_status: string | null;
  modules: Module[];
  total_lessons: number;
  completed_lessons: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function lessonIcon(type: string) {
  if (type === 'video') return <Video className="h-3.5 w-3.5 shrink-0" />;
  if (type === 'form_embed') return <FileText className="h-3.5 w-3.5 shrink-0" />;
  return <BookOpen className="h-3.5 w-3.5 shrink-0" />;
}

// Bilingual support: does any lesson carry a Spanish variant, and merge that
// variant over the English base for display (missing pieces fall back).
function courseHasSpanish(course: { modules: { lessons: Lesson[] }[] }): boolean {
  return course.modules.some((m) =>
    m.lessons.some((l) => {
      const es = l.translations?.es;
      return !!es && (!!es.title || Object.keys(es.content || {}).length > 0);
    }),
  );
}

function localizeLesson(lesson: Lesson, lang: 'en' | 'es'): Lesson {
  if (lang === 'en') return lesson;
  const es = lesson.translations?.es;
  if (!es) return lesson;
  const esContent = es.content || {};
  const content = Object.keys(esContent).length ? { ...lesson.content, ...esContent } : lesson.content;
  return { ...lesson, title: es.title || lesson.title, content };
}

function LessonPlayer({
  lesson,
  answers,
  setAnswer,
}: {
  lesson: Lesson;
  answers: Record<number, number | string>;
  setAnswer: (qi: number, v: number | string) => void;
}) {
  const content = lesson.content as Record<string, any>;

  if (lesson.content_type === 'slides') {
    const rawUrl = content.embed_url || content.url || '';
    const src = slidesEmbedSrc(content.provider, rawUrl);
    return (
      <div className="aspect-video w-full rounded-lg overflow-hidden border bg-gray-100">
        {src ? (
          <iframe src={src} className="w-full h-full" allowFullScreen allow="fullscreen" title={lesson.title} />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">No slides configured.</div>
        )}
      </div>
    );
  }

  if (lesson.content_type === 'quiz' || lesson.content_type === 'survey') {
    const questions = (content.questions as any[]) || [];
    if (questions.length === 0) {
      return <p className="text-sm text-gray-400">No questions configured.</p>;
    }
    return (
      <div className="space-y-5">
        {questions.map((q: any, qi: number) => (
          <div key={qi} className="border rounded-lg p-4">
            <p className="font-medium text-gray-800 mb-3">
              {qi + 1}. {q.prompt}
            </p>
            {(q.options || []).length === 0 ? (
              <textarea
                rows={3}
                value={typeof answers[qi] === 'string' ? (answers[qi] as string) : ''}
                onChange={(e) => setAnswer(qi, e.target.value)}
                placeholder="Escriba su respuesta… / Type your answer…"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="space-y-2">
                {(q.options || []).map((opt: string, oi: number) => (
                  <label
                    key={oi}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                      answers[qi] === oi ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`q-${qi}`}
                      checked={answers[qi] === oi}
                      onChange={() => setAnswer(qi, oi)}
                      className="accent-blue-600"
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (lesson.content_type === 'video') {
    const url = content.url || '';
    // Convert YouTube watch URLs to embed URLs
    const embedUrl = url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/');
    return (
      <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={lesson.title}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-white text-sm">
            No video URL provided.
          </div>
        )}
      </div>
    );
  }

  if (lesson.content_type === 'form_embed') {
    const src = content.embed_url || content.url || '';
    return (
      <div className="w-full rounded-lg overflow-hidden border">
        {src ? (
          <iframe src={src} className="w-full min-h-[600px]" title={lesson.title} />
        ) : (
          <div className="p-8 text-center text-gray-400 text-sm">No form URL configured.</div>
        )}
      </div>
    );
  }

  // Default: text/html content
  const html = content.html || content.body || '';
  const text = content.text || '';
  return (
    <div className="prose prose-sm max-w-none text-gray-800">
      {html ? (
        <div dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <p className="whitespace-pre-wrap">{text || 'No content available.'}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function CourseViewerPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();

  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [enrollmentId, setEnrollmentId] = useState<number | null>(null);
  const [activeLesson, setActiveLesson] = useState<Lesson | null>(null);
  const [lang, setLang] = useState<'en' | 'es'>('en');
  const [answers, setAnswers] = useState<Record<number, number | string>>({});
  const memberId = getMemberData()?.member_id ?? getMemberData()?.id ?? null;
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');

  const token = getMemberToken();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadCourse = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/member-portal/lms/courses/${courseId}`), { headers });
      if (!res.ok) throw new Error('Course not found');
      const data: CourseDetail = await res.json();
      setCourse(data);
      setEnrollmentId(data.enrollment_id);
      // Select first incomplete lesson (or first lesson if all done)
      const allLessons = data.modules.flatMap((m) => m.lessons);
      const firstIncomplete = allLessons.find((l) => !l.completed) ?? allLessons[0] ?? null;
      setActiveLesson(firstIncomplete);
    } catch (err: any) {
      setError(err.message || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login');
      return;
    }
    loadCourse();
  }, [navigate, loadCourse]);

  // clear the working answers when switching lessons
  useEffect(() => {
    setAnswers({});
  }, [activeLesson?.id]);

  const handleEnroll = async () => {
    if (!courseId) return;
    setEnrolling(true);
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/member-portal/lms/courses/${courseId}/enroll`), {
        method: 'POST',
        headers,
      });
      if (!res.ok) throw new Error('Enrollment failed');
      const data = await res.json();
      setEnrollmentId(data.enrollment_id);
      await loadCourse();
    } catch (err: any) {
      setError(err.message || 'Enrollment failed');
    } finally {
      setEnrolling(false);
    }
  };

  const handleCompleteLesson = async () => {
    if (!activeLesson || !enrollmentId) return;
    setCompleting(true);
    try {
      const isAssessment = activeLesson.content_type === 'quiz' || activeLesson.content_type === 'survey';
      let body: string | undefined;
      if (isAssessment) {
        const qs = ((activeLesson.content as any)?.questions as any[]) || [];
        body = JSON.stringify({
          answers: qs.map((q, i) => (i in answers ? answers[i] : (q?.options || []).length > 0 ? -1 : '')),
        });
      }
      const res = await fetch(
        getWeaveinApiUrl(
          `/api/member-portal/lms/lessons/${activeLesson.id}/complete?enrollment_id=${enrollmentId}`
        ),
        {
          method: 'POST',
          headers: body ? { ...headers, 'Content-Type': 'application/json' } : headers,
          body,
        }
      );
      if (!res.ok) throw new Error('Could not mark lesson complete');
      const data = await res.json().catch(() => ({} as any));
      if (isAssessment && data && data.completed === false) {
        setError(data.message || 'Score below passing — review and try again.');
      } else {
        setAnswers({});
        await loadCourse();
      }
    } catch (err: any) {
      setError(err.message || 'Could not mark lesson complete');
    } finally {
      setCompleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Loading course…</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-500">
          <p className="mb-4">{error || 'Course not found.'}</p>
          <Link to="/member/courses">
            <Button variant="outline" size="sm">Back to courses</Button>
          </Link>
        </div>
      </div>
    );
  }

  const progressPct =
    course.total_lessons > 0 ? Math.round((course.completed_lessons / course.total_lessons) * 100) : 0;
  const enrolled = enrollmentId !== null;
  const allLessons = course.modules.flatMap((m) => m.lessons);
  const hasSpanish = courseHasSpanish(course);
  const displayLesson = activeLesson ? localizeLesson(activeLesson, lang) : null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* ── Top bar ── */}
      <header className="bg-white border-b px-4 py-2 flex items-center gap-3 shrink-0 z-10">
        <Link to="/member/courses">
          <Button variant="ghost" size="sm" className="gap-1">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold text-gray-900 text-sm truncate">{course.title}</h1>
          {enrolled && (
            <div className="flex items-center gap-2 mt-0.5">
              <Progress value={progressPct} className="h-1.5 w-32" />
              <span className="text-xs text-gray-400">
                {course.completed_lessons}/{course.total_lessons} lessons
              </span>
            </div>
          )}
        </div>
        {hasSpanish && (
          <div className="flex items-center rounded-lg border p-0.5 shrink-0" title="Course language">
            {(['en', 'es'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-2 py-0.5 rounded-md text-xs font-medium transition-colors ${
                  lang === l ? 'bg-teal-100 text-teal-800' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {l === 'en' ? 'English' : 'Español'}
              </button>
            ))}
          </div>
        )}
        {!enrolled && (
          <Button size="sm" onClick={handleEnroll} disabled={enrolling} className="gap-1 shrink-0">
            <Play className="h-3.5 w-3.5" />
            {enrolling ? 'Enrolling…' : 'Enroll'}
          </Button>
        )}
        {enrolled && course.enrollment_status === 'completed' && (
          <Badge className="bg-green-100 text-green-700 border-green-200 shrink-0">
            <CheckCircle className="h-3 w-3 mr-1" />
            Complete
          </Badge>
        )}
      </header>

      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-red-600 text-xs">{error}</div>
      )}

      {/* ── Body: sidebar + player ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 shrink-0 border-r bg-gray-50 overflow-y-auto">
          <div className="p-3">
            {course.modules.map((mod) => (
              <div key={mod.id} className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 px-1">
                  {mod.title}
                </p>
                <ul className="space-y-0.5">
                  {mod.lessons.map((lesson) => {
                    const isActive = activeLesson?.id === lesson.id;
                    const isLocked = !enrolled;
                    return (
                      <li key={lesson.id}>
                        <button
                          onClick={() => !isLocked && setActiveLesson(lesson)}
                          disabled={isLocked}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors ${
                            isActive
                              ? 'bg-blue-100 text-blue-800 font-medium'
                              : isLocked
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {isLocked ? (
                            <Lock className="h-3.5 w-3.5 shrink-0 text-gray-300" />
                          ) : lesson.completed ? (
                            <CheckCircle className="h-3.5 w-3.5 shrink-0 text-green-500" />
                          ) : (
                            <Circle className="h-3.5 w-3.5 shrink-0 text-gray-300" />
                          )}
                          {lessonIcon(lesson.content_type)}
                          <span className="truncate leading-tight">{localizeLesson(lesson, lang).title}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </aside>

        {/* Lesson player */}
        <main className="flex-1 overflow-y-auto p-6">
          {!enrolled ? (
            <div className="max-w-lg mx-auto text-center py-16">
              <BookOpen className="h-12 w-12 text-gray-200 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-700 mb-2">Enroll to get started</h2>
              {course.description && <p className="text-sm text-gray-500 mb-6">{course.description}</p>}
              <Button onClick={handleEnroll} disabled={enrolling} className="gap-2">
                <Play className="h-4 w-4" />
                {enrolling ? 'Enrolling…' : 'Enroll in this course'}
              </Button>
            </div>
          ) : activeLesson && displayLesson ? (
            <div className="w-full max-w-5xl mx-auto">
              <div className="flex items-start justify-between gap-3 mb-4">
                <h2 className="text-xl font-semibold text-ink leading-snug">{displayLesson.title}</h2>
                <Badge variant="outline" className="text-xs shrink-0 capitalize">
                  {displayLesson.content_type.replace('_', ' ')}
                </Badge>
              </div>

              {displayLesson.content_type === 'platform_survey' ? (
                (displayLesson.content as any)?.survey_id ? (
                  <SurveyFill
                    surveyId={String((displayLesson.content as any).survey_id)}
                    memberId={memberId ? String(memberId) : null}
                    embedded
                    onCompleted={handleCompleteLesson}
                  />
                ) : (
                  <p className="text-sm text-gray-400">No survey linked to this component yet.</p>
                )
              ) : (
                <LessonPlayer
                  lesson={displayLesson}
                  answers={answers}
                  setAnswer={(qi, v) => setAnswers((a) => ({ ...a, [qi]: v }))}
                />
              )}

              <div className="mt-6 flex items-center justify-between">
                {/* Prev / Next */}
                <div className="flex gap-2">
                  {(() => {
                    const idx = allLessons.findIndex((l) => l.id === activeLesson.id);
                    const prev = allLessons[idx - 1];
                    const next = allLessons[idx + 1];
                    return (
                      <>
                        {prev && (
                          <Button variant="outline" size="sm" onClick={() => setActiveLesson(prev)}>
                            ← Previous
                          </Button>
                        )}
                        {next && (
                          <Button variant="outline" size="sm" onClick={() => setActiveLesson(next)}>
                            Next →
                          </Button>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Mark complete — platform surveys complete on submit above */}
                {activeLesson.content_type === 'platform_survey' && !activeLesson.completed ? (
                  <span className="text-xs text-gray-400">Submit the survey above to complete this component.</span>
                ) : !activeLesson.completed ? (
                  <Button
                    size="sm"
                    onClick={handleCompleteLesson}
                    disabled={completing}
                    className="gap-1.5"
                  >
                    <CheckCircle className="h-4 w-4" />
                    {completing
                      ? 'Saving…'
                      : activeLesson.content_type === 'quiz' || activeLesson.content_type === 'survey'
                      ? 'Submit answers'
                      : 'Mark complete'}
                  </Button>
                ) : (
                  <Badge className="bg-green-50 text-green-700 border-green-200 gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Completed
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400 text-sm">Select a lesson to begin.</div>
          )}
        </main>
      </div>
    </div>
  );
}
